import { Injectable } from '@angular/core';
import { NGXLogger as LoggerService } from "ngx-logger";
import { AngularFireDatabase, AngularFireList } from '@angular/fire/compat/database';
import { firstValueFrom, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, map, skip, tap } from 'rxjs/operators';
import { VoteClass, VoteData, VotePrivateData } from 'whocan-lib';
import { PathService } from '../../core/path.service';
import { CurrentPollService } from '../../core/current-poll.service';
import { HeaderService } from '../../shared/poll-header/header.service';
import firebase from 'firebase/compat/app';
import { EssErrorService } from 'ngx-essentia';
import { OptionsService } from '../../shared/options/options.service';
import { isPermissionDenied } from '../../../shared/services/db-errors';

/**
 * Rollout flag for the votePrivate contact field split (workorder-vote-privacy-contact-split-2026-08-30):
 * while true, persistVote writes userEmail/userPhone/privateComment to BOTH the public votes node
 * (old clients, rules still open) and the new votePrivate node. Set to false at cutover day
 * (task 6 of the workorder), after the backfill + delete sweep have run on prod.
 */
export const DUAL_WRITE_CONTACT_FIELDS = false;

@Injectable({
    providedIn: 'root'
})
export class VotesService {
    public _voteClassList: VoteClass[] = [];
    private _isLoading: boolean = false;
    private _realtimeSubscription: Subscription | null = null;
    private _optionsUpdatedSubscription: Subscription | null = null;
    private _votesUpdated$ = new Subject<void>();
    /**
     * The separately loaded contact parts (votePrivate node) by voter userId.
     * Only filled for the poll owner (plus delegates) — the rules deny the list
     * read to everybody else. Used to re-apply the contact fields whenever the
     * voteClassList is rebuilt from a realtime update.
     */
    private _privateDataByUserId: Map<string, VotePrivateData> = new Map();
    private _votePrivateSubscription: Subscription | null = null;

    get votesUpdated$(): Observable<void> {
        return this._votesUpdated$.asObservable();
    }
    constructor(
        private logger: LoggerService,
        private afd: AngularFireDatabase,
        private pathService: PathService,
        private currentPollService: CurrentPollService,
        private headerService: HeaderService,
        private errorService: EssErrorService,
        private optionsService: OptionsService
    ) {
    }

    /**
     * The votes exactly as the database holds them - rebuilt on every server update, nothing added
     * locally. The current voter's form is UserVoteService.userVote, a vote object of its own that
     * never lies in this list (concept-own-form-state-2026-09-18).
     */
    get voteClassList(): VoteClass[] {
        return this._voteClassList
    }

    /** the votes with a non-empty public comment, in the order of voteClassList (comment list and its counter in the box title) */
    get publicComments(): VoteClass[] {
        return (this._voteClassList ?? []).filter(vote => vote.publicComment?.trim())
    }

    public async load() {
        if (!this.isLoading) { // this is async so we avoid it's called twice at the same time
            this.logger.log('load : ', this._voteClassList);
            this._isLoading = true
            this._voteClassList = [];
            const votesDataList = await firstValueFrom(this.obvservable)
            votesDataList.forEach(votesData => {
                this._voteClassList.push(new VoteClass(votesData, this.optionsService.optionList.lengthIncludingDeleted))

            })
            this._voteClassList.sort((voteA, voteB) => {
                return voteA.updatedAt > voteB.updatedAt ? 1 : -1;
            })
            await this.loadPrivateData();
            this.logger.log('loadVotesData : ', this._voteClassList);
            this._isLoading = false
        } else {
            this.logger.log('load : is loading already')
        }
    }

    public uninitialize() {
        this.unsubscribeFromRealtimeUpdates();
        this._voteClassList = []
        this._privateDataByUserId.clear();
    }

    /**
     * Loads the contact parts (votePrivate/{ownerId}/{pollId}) and merges them into the
     * voteClassList. Owner (plus delegates) only — the rules deny the list read to everybody
     * else, so we do not even try. A missing node is fine (polls from before the backfill).
     */
    private async loadPrivateData(): Promise<void> {
        if (!this.currentPollService.isOwner) return;
        try {
            const privateByUserId = await firstValueFrom(
                this.afd.object<{ [voterId: string]: VotePrivateData }>(this.pathService.getVotePrivatePath()).valueChanges()
            );
            this.mergePrivateData(privateByUserId);
        } catch (error) {
            // never break the results page over the contact columns
            if (isPermissionDenied(error)) {
                // the read grant went away between the isOwner check and the read (token swap
                // during an account merge, delegation revoked) - expected, nothing to report
                this.logger.warn('loadPrivateData: read denied for the votePrivate node', error);
                return;
            }
            this.logger.error('loadPrivateData failed', error);
        }
    }

    private mergePrivateData(privateByUserId: { [voterId: string]: VotePrivateData } | null): void {
        this._privateDataByUserId.clear();
        if (privateByUserId) {
            Object.keys(privateByUserId).forEach(voterId => {
                this._privateDataByUserId.set(voterId, privateByUserId[voterId]);
            });
        }
        this.applyPrivateDataToVoteList();
    }

    private applyPrivateDataToVoteList(): void {
        this._voteClassList.forEach(vote => {
            const privateData = this._privateDataByUserId.get(vote.userId);
            if (privateData) vote.applyPrivateData(privateData);
        });
    }

    public subscribeToRealtimeUpdates(): void {
        this.unsubscribeFromRealtimeUpdates();
        this._realtimeSubscription = this.obvservable.pipe(
            skip(1) // skip first emission since we already loaded via load()
        ).subscribe(votesDataList => {
            this.logger.log('realtime update received, count:', votesDataList.length);
            this.updateVoteClassListFromRealtime(votesDataList);
        });
        if (this.currentPollService.isOwner) {
            // keep the contact columns of the result matrix live as well (owner only,
            // everybody else has no read grant on the votePrivate node)
            this._votePrivateSubscription = this.afd.object<{ [voterId: string]: VotePrivateData }>(
                this.pathService.getVotePrivatePath()
            ).valueChanges().pipe(
                skip(1) // first emission was handled by loadPrivateData()
            ).subscribe({
                next: privateByUserId => {
                    this.logger.log('realtime votePrivate update received');
                    this.mergePrivateData(privateByUserId);
                    this._votesUpdated$.next();
                },
                // Without this the revoked listen ends up in the global ErrorHandler as an
                // uncaught error. The server revokes it whenever the token on the connection
                // loses the read grant - an account merge swaps the token and the delegation
                // claim of the new account arrives only afterwards. The already loaded contact
                // data stays valid and is kept; only the live updates end here.
                error: error => {
                    if (isPermissionDenied(error)) {
                        this.logger.warn('votePrivate listen revoked, contact columns are no longer live', error);
                    } else {
                        this.logger.error('votePrivate realtime subscription failed - ' + error);
                    }
                    this._votePrivateSubscription = null;
                },
            });
        }
        // a remotely added option makes every loaded vote one choice too short. This runs
        // before the components react to the same event, so they already see padded votes
        // (the vote form is not in the list - UserVoteService pads it in its own subscription)
        this._optionsUpdatedSubscription = this.optionsService.optionsUpdated$.subscribe(() => {
            this.padChoiceLists();
        });
    }

    public unsubscribeFromRealtimeUpdates(): void {
        this._realtimeSubscription?.unsubscribe();
        this._realtimeSubscription = null;
        this._votePrivateSubscription?.unsubscribe();
        this._votePrivateSubscription = null;
        this._optionsUpdatedSubscription?.unsubscribe();
        this._optionsUpdatedSubscription = null;
    }

    /**
     * Grows every vote's choice list to the current number of options. initChoiceList only
     * appends, so answers that were already given - saved or not - stay untouched.
     */
    public padChoiceLists(): void {
        const numberOfOptions = this.optionsService.optionList.lengthIncludingDeleted;
        this._voteClassList.forEach(vote => vote.initChoiceList(numberOfOptions));
    }

    /**
     * Rebuilds the list from the server data, no exceptions. What happens to the current voter's
     * form (take the server state over or keep the draft) is decided by UserVoteService, which
     * listens to votesUpdated$ as well.
     */
    private updateVoteClassListFromRealtime(votesDataList: VoteData[]): void {
        const newList: VoteClass[] = votesDataList.map(voteData =>
            new VoteClass(voteData, this.optionsService.optionList.lengthIncludingDeleted));

        newList.sort((voteA, voteB) => {
            return voteA.updatedAt > voteB.updatedAt ? 1 : -1;
        });

        this._voteClassList = newList;
        // the rebuilt VoteClass instances only carry the public part - re-apply the
        // separately loaded contact fields (owner only, map is empty otherwise)
        this.applyPrivateDataToVoteList();
        this._votesUpdated$.next();
        this.logger.log('realtime voteClassList updated, count:', this._voteClassList.length);
    }

    get isLoading(): boolean {
        return this._isLoading
    }

    /**
     * True when the current user may read the whole votes list under the database rules:
     * the owner (plus delegates, covered by isOwner/hasEditRights), every participant while
     * showParticipants is not switched off (`!== false` — legacy headers miss the field,
     * same semantics as the rules) and sample polls (their matrix page is a public demo).
     * The header is loaded before the votes (see PollService.loadPollData), so the
     * headerClass is reliable here.
     *
     * Public: PollService uses it to decide whether the voteCounts fallback must be
     * subscribed (limit checks for participants who cannot read the list, see
     * workorder-option-counts-2026-08-31).
     */
    public get canReadVotesList(): boolean {
        return this.currentPollService.isOwner
            || this.headerService.headerClass?.showParticipants !== false
            || this.headerService.headerClass?.isSamplePoll;
    }

    get obvservable(): Observable<VoteData[]> {
        if (this.canReadVotesList) {
            const ref: AngularFireList<VoteData> = this.afd.list(this.pathService.getVotesPath());
            return ref.valueChanges().pipe(
                // a permission denied here (e.g. the owner toggled showParticipants off while
                // we are subscribed) must not toast or spam the console - degrade to the own
                // vote node, which stays readable. An empty list would drop the own vote as
                // well, and the vote form would take that for a vote deleted elsewhere
                // (UserVoteService.onVotesUpdated) and empty itself
                catchError(error => {
                    this.logger.warn('votes list read denied, falling back to the own vote', error);
                    return this.ownVoteObservable;
                }));
        }
        return this.ownVoteObservable;
    }

    private get ownVoteObservable(): Observable<VoteData[]> {
        // participant on a poll with showParticipants switched off: the rules deny the list
        // read, so only the own vote node is subscribed (the matrix filter stays in place as
        // second line of defense). getVoteUserId covers the delegated-vote case.
        return this.afd.object<VoteData>(
            this.pathService.getUserVotePath(this.currentPollService.getVoteUserId())
        ).valueChanges().pipe(
            map(voteData => voteData ? [voteData] : []),
            catchError(error => {
                this.logger.warn('own vote read denied, falling back to empty list', error);
                return of([] as VoteData[]);
            }));
    }

    public async getVotes(ownerId: string, pollId: string): Promise<VoteData[]> {
        const ref: AngularFireList<VoteData> = this.afd.list(this.pathService.getVotesPath(ownerId, pollId));
        return firstValueFrom(ref.valueChanges());
    }

    public async deleteVotes(ownerId: string, pollId: string): Promise<void> {
        // one atomic update: the contact parts in votePrivate must not outlive the votes
        await this.afd.database.ref().update({
            [this.pathService.getVotesPath(ownerId, pollId)]: null,
            [this.pathService.getVotePrivatePath(ownerId, pollId)]: null,
        });
        this.logger.log('getVotesPath: ownerId:  ' + ownerId + '; pollId: ' + pollId);
    }

    /**
     * Rejects when the write fails (e.g. the database rules refuse a vote on a
     * closed poll). Callers must await it — a swallowed error would show the
     * voter a success message for a vote that was never stored.
     *
     * The vote is written as one atomic multi path update: the public part into the
     * votes node, the contact part (userEmail/userPhone/privateComment) into the access
     * restricted votePrivate node. While DUAL_WRITE_CONTACT_FIELDS is true the contact
     * fields additionally stay in the public node (rollout phase, see the flag's comment).
     */
    public async persistVote(vote: VoteClass): Promise<void> {
        const userId: string = vote.userId;
        if (!userId) { throw this.errorService.newError('persistVote: userId is empty'); }
        const publicData: VoteData = DUAL_WRITE_CONTACT_FIELDS ? vote.data : vote.dataPublic;
        // the server timestamp is a placeholder object ({".sv": "timestamp"}) until the realtime
        // echo replaces it - it goes into the payload copy only, never onto the vote itself:
        // the owner's own vote stays bound in the activity list and a DatePipe on the
        // placeholder throws NG02100 (InvalidPipeArgument) on the next change detection
        if (!vote.createdAt) { publicData.createdAt = firebase.database.ServerValue.TIMESTAMP; } // new vote
        publicData.updatedAt = firebase.database.ServerValue.TIMESTAMP;
        const privateData: VotePrivateData | null = vote.dataPrivate;
        const updates: { [path: string]: unknown } = {};
        // merge semantics per field (like the previous ref.update(vote.data)), so fields
        // another writer added to the node are not wiped by a whole-node replace.
        // undefined fields are skipped: firebase rejects them ("values argument contains
        // undefined in property ..."), and a field the vote does not carry is simply left as is
        Object.keys(publicData).forEach(field => {
            if (publicData[field] === undefined) return;
            updates[`${this.pathService.getUserVotePath(userId)}/${field}`] = publicData[field];
        });
        if (privateData) {
            Object.keys(privateData).forEach(field => {
                if (privateData[field] === undefined) return;
                updates[`${this.pathService.getUserVotePrivatePath(userId)}/${field}`] = privateData[field];
            });
        }
        this.logger.log('persist : ', this.pathService.getUserVotePath(userId), vote, publicData, privateData);
        await this.afd.database.ref().update(updates);
    }
}
